import { ArrowDown, Search } from "lucide-react";
import { useDeferredValue, useState } from "react";

import type { UsageStatsResponse } from "@/lib/types";

import { formatInt, formatTokens, getEntryPointColor } from "./formatters";

const SYSTEM_OWNER_EMAILS = new Set(["system@sherlog.local", "cli@sherlog.local"]);

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-cyber-border bg-cyber-surface/30 rounded-lg border px-3 py-2">
      <div className="text-cyber-muted/70 text-[10px] tracking-wide uppercase">{label}</div>
      <div className="text-cyber-text mt-1 text-sm">{value}</div>
    </div>
  );
}

function ownerLabel(ownerEmail: string): string {
  return SYSTEM_OWNER_EMAILS.has(ownerEmail) ? "System" : ownerEmail;
}

export function CircuitUsageStats({ data }: { data: UsageStatsResponse }) {
  const rows = data.automatedUsage;
  const [search, setSearch] = useState("");
  const [owner, setOwner] = useState("all");
  const deferredSearch = useDeferredValue(search).trim().toLowerCase();
  const ownerOptions = [...new Set(rows.map((row) => row.ownerEmail))]
    .filter((email) => !SYSTEM_OWNER_EMAILS.has(email))
    .sort((a, b) => a.localeCompare(b));
  const hasSystemOwner = rows.some((row) => SYSTEM_OWNER_EMAILS.has(row.ownerEmail));
  const visibleRows = rows.filter((row) => {
    if (owner === "system" && !SYSTEM_OWNER_EMAILS.has(row.ownerEmail)) return false;
    if (owner !== "all" && owner !== "system" && row.ownerEmail !== owner) return false;
    if (!deferredSearch) return true;

    return [
      row.name,
      row.ownerEmail,
      ownerLabel(row.ownerEmail),
      row.kind,
      row.source,
      row.id,
    ].some((value) => value?.toLowerCase().includes(deferredSearch));
  });
  const totalTokens = rows.reduce((total, row) => total + row.totalTokens, 0);
  const totalRuns = rows.reduce((total, row) => total + row.runs, 0);
  const maxTokens = rows[0]?.totalTokens ?? 0;
  const shareOfUsage =
    data.totals.totalTokens > 0 ? (totalTokens / data.totals.totalTokens) * 100 : 0;

  return (
    <>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <StatCard label="Circuit tokens" value={formatTokens(totalTokens)} />
        <StatCard label="Share of all usage" value={`${shareOfUsage.toFixed(1)}%`} />
        <StatCard label="Runs" value={formatInt(totalRuns)} />
        <StatCard
          label="Avg tokens / run"
          value={formatTokens(totalRuns > 0 ? totalTokens / totalRuns : 0)}
        />
      </div>

      <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-cyber-muted/80 text-xs tracking-wide uppercase">
            Token consumption by circuit
          </h3>
          <span className="text-cyber-muted/60 text-[10px]">{formatInt(rows.length)} circuits</span>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          <div className="relative min-w-56 flex-1">
            <Search
              size={13}
              className="text-cyber-muted pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, owner, type, or ID"
              aria-label="Search circuit usage"
              className="border-cyber-border bg-cyber-bg/50 text-cyber-text placeholder:text-cyber-muted/60 focus:border-cyber-cyan/60 w-full rounded border py-2 pr-3 pl-8 text-xs transition-colors outline-none"
            />
          </div>
          <select
            value={owner}
            onChange={(event) => setOwner(event.target.value)}
            aria-label="Filter circuit usage by owner"
            className="border-cyber-border bg-cyber-bg text-cyber-text focus:border-cyber-cyan/60 max-w-56 rounded border px-3 py-2 text-xs outline-none"
          >
            <option value="all">All owners</option>
            {hasSystemOwner && <option value="system">System</option>}
            {ownerOptions.map((email) => (
              <option key={email} value={email}>
                {email}
              </option>
            ))}
          </select>
          <span className="text-cyber-muted self-center text-[10px] whitespace-nowrap">
            {formatInt(visibleRows.length)} of {formatInt(rows.length)}
          </span>
        </div>

        {rows.length === 0 ? (
          <p className="text-cyber-muted/60 py-10 text-center text-xs">
            No circuit token usage in this period.
          </p>
        ) : visibleRows.length === 0 ? (
          <p className="text-cyber-muted/60 py-10 text-center text-xs">
            No circuits match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-xs">
              <thead>
                <tr className="border-cyber-border/50 text-cyber-muted/70 border-b text-left text-[10px] tracking-wide uppercase">
                  <th className="pr-4 pb-2 font-medium">Circuit</th>
                  <th className="pr-4 pb-2 font-medium">Owner</th>
                  <th className="pr-4 pb-2 text-right font-medium">Runs</th>
                  <th
                    className="pr-4 pb-2 text-right font-medium"
                    aria-sort="descending"
                    title="Sorted by total tokens, highest to lowest"
                  >
                    <span className="inline-flex items-center justify-end gap-1">
                      Total <ArrowDown size={10} aria-hidden="true" />
                    </span>
                  </th>
                  <th className="pr-4 pb-2 text-right font-medium">Input</th>
                  <th className="pr-4 pb-2 text-right font-medium">Output</th>
                  <th className="pr-4 pb-2 text-right font-medium">Avg / run</th>
                  <th className="pb-2 text-right font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => {
                  const color = getEntryPointColor(row.kind);
                  const width =
                    maxTokens > 0 ? Math.max((row.totalTokens / maxTokens) * 100, 2) : 0;
                  const systemOwned = SYSTEM_OWNER_EMAILS.has(row.ownerEmail);

                  return (
                    <tr key={`${row.kind}:${row.id}`} className="border-cyber-border/20 border-b">
                      <td className="min-w-64 py-2.5 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="text-cyber-text truncate" title={row.name}>
                            {row.name}
                          </span>
                          <span
                            className={`shrink-0 rounded border border-current/30 px-1.5 py-0.5 text-[9px] tracking-wider uppercase ${color.label}`}
                          >
                            {row.kind}
                          </span>
                          {systemOwned && (
                            <span className="border-cyber-magenta/30 bg-cyber-magenta/10 text-cyber-magenta shrink-0 rounded border px-1.5 py-0.5 text-[9px] tracking-wider uppercase">
                              System
                            </span>
                          )}
                          {row.source && (
                            <span className="text-cyber-muted/60 shrink-0 text-[9px] uppercase">
                              {row.source} · {row.id.slice(-8)}
                            </span>
                          )}
                        </div>
                        <div className="bg-cyber-bg/80 mt-1 h-1.5 w-full overflow-hidden rounded-full">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${width}%`, backgroundColor: color.bar }}
                          />
                        </div>
                      </td>
                      <td
                        className="text-cyber-muted max-w-48 truncate py-2.5 pr-4"
                        title={row.ownerEmail}
                      >
                        {ownerLabel(row.ownerEmail)}
                      </td>
                      <td className="text-cyber-muted py-2.5 pr-4 text-right">
                        {formatInt(row.runs)}
                      </td>
                      <td className={`py-2.5 pr-4 text-right font-medium ${color.label}`}>
                        {formatTokens(row.totalTokens)}
                      </td>
                      <td className="text-cyber-muted py-2.5 pr-4 text-right">
                        {formatTokens(row.inputTokens)}
                      </td>
                      <td className="text-cyber-muted py-2.5 pr-4 text-right">
                        {formatTokens(row.outputTokens)}
                      </td>
                      <td className="text-cyber-muted py-2.5 pr-4 text-right">
                        {formatTokens(row.avgTokensPerRun)}
                      </td>
                      <td className="text-cyber-muted py-2.5 text-right">
                        {row.totalCostUsd > 0 ? USD_FORMATTER.format(row.totalCostUsd) : "Unknown"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
